import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { Listbox, Transition } from "@headlessui/react";
import { Disclosure } from "@headlessui/react";
import { Caret, SvgInfoIcon } from "@routes/IconeSvg";

import BlackTooltip from "@components/tooltip/BlackTooltip";

const className = {
  normal:
    "arh-bg-white arh-border-greyBorderInput arh-text-darkColor placeholder:arh-text-colorPlaceholderInputSimple",
  invalid:
    "arh-bg-invalidBackgroundInput arh-border-invalidColorTextInput arh-text-invalidColorTextInput placeholder:arh-text-invalidColorTextInput",
  disabled:
    "arh-border-greyDisabledBorder arh-bg-greyDisabled arh-text-greyDisabledText"
};

const DropdownCategorieType = ({
  name,
  options = [],
  multiple = false,
  zIndex = 1,
  label = "Catégorie / Type",
  placeholder = "Sélectionner...",
  noOpen = false,
  customButtonClass = "",
  customButtonClassProps = "",
  disabled = false,
  readOnly = false,
  invalid,
  onChangeValue,
  value,
  customTranslator = null
}) => {
  const [selected, setSelected] = useState(multiple ? [] : null);
  const [isTopView, setIsTopView] = useState(false);
  const wrapperRef = useRef(null);

  // Sync valeur externe (ex: depuis react-hook-form)
  useEffect(() => {
    setSelected(multiple ? (Array.isArray(value) ? value : []) : value);
  }, [value]);

  const handleClick = () => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 150;
      const spaceAbove = rect.top;
      const spaceBelow = viewportHeight - rect.bottom;
      setIsTopView(spaceBelow < dropdownHeight && spaceAbove > dropdownHeight);
    }
  };

  const toggleValue = (category) => {
    if (multiple) {
      const updated = selected.some((s) => s.value === category.value)
        ? selected.filter((s) => s.value !== category.value)
        : [...selected, category];
      setSelected(updated);
      onChangeValue(updated);
    } else {
      setSelected(category);
      onChangeValue(category);
    }
  };

  const translate = useCallback(
    (v) =>
      typeof v === "string" && typeof customTranslator === "function"
        ? customTranslator(v)
        : v,
    [customTranslator]
  );

  const displayedValue = useMemo(() => {
    if (multiple) {
      if (selected.length === 0) {
        return "";
      }

      return selected.map((s) => getCategoryName(s)).join(", ");
    }
    return getCategoryName(selected);
  }, [selected, translate]);

  function getCategoryName(category) {
    if (!category) {
      return "";
    }

    if (category.parent) {
      return `${translate(category.parent)}/${translate(category.name)}`;
    }

    return `${translate(category.name)}`;
  }

  const isSelected = (category) => {
    return multiple
      ? selected.some((s) => s.value === category.value)
      : // @ts-ignore
        selected?.value === category.value;
  };

  return (
    <Listbox
      value={selected}
      onChange={() => {}}
      multiple={multiple}
    >
      {({ open }) => (
        <div
          ref={wrapperRef}
          className="arh-relative arh-w-full"
          style={{ zIndex }}
        >
          <label
            className={`arh-mb-1 arh-w-full arh-text-left arh-font-poppins arh-text-xs arh-font-medium ${invalid ? "arh-text-invalidColorTextInput" : "arh-text-black"}`}
          >
            {label}
          </label>

          <Listbox.Button
            onClick={handleClick}
            className={`arh-relative arh-flex arh-w-full arh-items-center ${noOpen ? "arh-pointer-events-none" : "arh-cursor-pointer"} arh-rounded arh-border arh-border-solid arh-px-6 arh-py-3 arh-pr-8 arh-text-left arh-font-poppins arh-text-xs arh-font-normal 
              focus:arh-outline-none focus-visible:arh-border-greyBorderInput focus-visible:arh-ring-2 focus-visible:arh-ring-greyBorderInput focus-visible:arh-ring-opacity-75 focus-visible:arh-ring-offset-2 
              focus-visible:arh-ring-offset-greyColor disabled:arh-pointer-events-none disabled:arh-cursor-none disabled:arh-border-greyDisabledBorder disabled:arh-bg-greyDisabled disabled:arh-text-greyDisabledText 
              ${customButtonClass} ${customButtonClassProps} ${!disabled ? (invalid ? className.invalid : className.normal) : className.disabled}`}
          >
            <input
              className={`arh-pointer-events-none arh-block arh-h-full arh-w-full arh-max-w-full arh-truncate arh-text-ellipsis arh-border-0 arh-bg-transparent arh-text-darkColor placeholder:arh-text-xs ${invalid ? "placeholder:arh-text-invalidColorTextInput" : "placeholder:arh-text-colorPlaceholderInputSimple"}`}
              type="text"
              placeholder={placeholder}
              value={displayedValue}
              onChange={() => {}}
              disabled={readOnly}
            />
            <span className="arh-pointer-events-none arh-absolute arh-inset-y-0 arh-right-0 arh-flex arh-items-center arh-pr-18">
              <Caret
                className={`${open ? "arh-rotate-90" : "arh-rotate-0"}`}
                aria-hidden="true"
              />
            </span>
          </Listbox.Button>

          <Transition
            as={Fragment}
            show={open}
            leave="arh-transition arh-ease-in arh-duration-100"
            leaveFrom="arh-opacity-100"
            leaveTo="arh-opacity-0"
          >
            <Listbox.Options
              className={`${isTopView ? "arh-bottom-full arh-mb-1" : "arh-mt-1"} arh-absolute arh-z-20 arh-max-h-60 arh-w-full arh-overflow-y-auto arh-rounded arh-bg-white arh-px-4 arh-py-4 arh-text-xs arh-shadow-xs`}
            >
              {options.map((category, index) =>
                category.child?.length > 0 ? (
                  <Disclosure key={category.value}>
                    {({ open: openDisclosure }) => (
                      <>
                        <Disclosure.Button
                          className={`arh-relative arh-mb-[0.2rem] arh-flex arh-w-full arh-cursor-pointer arh-select-none arh-list-none arh-items-center arh-justify-between arh-truncate arh-rounded arh-border-0 
                              arh-bg-transparent arh-px-4 arh-py-2 arh-text-left arh-font-poppins arh-text-xs arh-font-normal arh-text-darkColor arh-transition hover:arh-bg-gray-200`}
                        >
                          <span>{category.name}</span>
                          <Caret
                            className={`${openDisclosure ? "arh-rotate-90" : "arh-rotate-0"}`}
                          />
                        </Disclosure.Button>
                        <Disclosure.Panel>
                          {category.child.map((nestedCategory) => (
                            <div
                              key={nestedCategory.value}
                              onClick={() => toggleValue(nestedCategory)}
                              className={`arh-relative arh-mb-[0.2rem] arh-flex arh-cursor-pointer arh-select-none 
                                  arh-list-none arh-items-center arh-justify-between arh-truncate arh-rounded arh-px-4 arh-py-2 arh-text-left arh-font-poppins arh-text-xs arh-font-normal
                                  arh-text-darkColor hover:arh-bg-gray-200 active:arh-bg-greyActive ${isSelected(nestedCategory) ? "arh-bg-greyActive" : ""}`}
                            >
                              <span className="arh-line-clamp-2 arh-flex-1">
                                {translate(nestedCategory.name)}
                              </span>
                              <BlackTooltip
                                title={nestedCategory.description}
                                placement="top-end"
                              >
                                {SvgInfoIcon()}
                              </BlackTooltip>
                            </div>
                          ))}
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                ) : (
                  <div
                    key={category.value}
                    onClick={() => toggleValue(category)}
                    className={`arh-relative arh-mb-[0.2rem] arh-flex arh-w-full arh-cursor-pointer arh-select-none arh-list-none arh-items-center arh-justify-between arh-truncate arh-rounded arh-border-0 
                              arh-bg-transparent arh-px-4 arh-py-2 arh-text-left arh-font-poppins arh-text-xs arh-font-normal arh-text-darkColor arh-transition hover:arh-bg-gray-200 ${isSelected(category) ? "arh-bg-greyActive" : ""}`}
                  >
                    {translate(category.name)}
                  </div>
                )
              )}
            </Listbox.Options>
          </Transition>
        </div>
      )}
    </Listbox>
  );
};

export default DropdownCategorieType;
